import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Home, Server, Globe, Mail, ArrowRight, Package, CheckCircle, Radio } from 'lucide-react';
import { useAcademyStore } from '../../../store/academyStore';

export const ClientServerMap = () => {
    const { completeModule } = useAcademyStore();
    const [step, setStep] = useState(0);
    const [serverProcessing, setServerProcessing] = useState(false);

    // Auto-complete logic when the full cycle finishes
    useEffect(() => {
        if (step === 4) {
            completeModule('architecture', 100, 'badge_architecture');
        }
    }, [step, completeModule]);

    const handleSend = () => {
        setStep(1); // Traveling
        setTimeout(() => {
            setStep(2); // Arrived at Server
            setServerProcessing(true);
            setTimeout(() => {
                setServerProcessing(false);
                setStep(3); // Returning
                setTimeout(() => setStep(4), 1000); // Back at Client (Pizza received)
            }, 2500); // Processing time
        }, 1500); // Travel time
    };

    const reset = () => {
        setStep(0);
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-900 via-indigo-950 to-slate-900 font-sans text-white flex flex-col">
            {/* Nav */}
            <div className="bg-slate-900/50 backdrop-blur-md border-b border-white/10 px-8 py-4 flex justify-between items-center sticky top-0 z-50">
                <Link to="/" className="flex items-center gap-2 text-slate-300 font-bold hover:text-green-400 transition-colors">
                    <Home size={20} /> Dashboard
                </Link>
                <div className="bg-blue-500/20 text-blue-300 px-4 py-2 rounded-full text-sm font-bold border border-blue-500/30 flex items-center gap-2">
                    <Radio size={18} /> Module: The Network Map
                </div>
            </div>

            <main className="flex-1 flex flex-col items-center justify-center p-8 max-w-7xl mx-auto w-full">

                <h1 className="text-5xl font-black text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-cyan-300 mb-6 drop-shadow-sm text-center">
                    Client-Server Architecture 🌐
                </h1>
                <p className="text-center text-slate-400 mb-16 max-w-2xl mx-auto text-xl leading-relaxed">
                    The Internet is a conversation. You (the <strong>Client</strong>) make a request, and a computer far away (the <strong>Server</strong>) sends back an answer.
                </p>

                {/* VISUALIZATION STAGE */}
                <div className="w-full bg-slate-800/50 rounded-3xl p-12 lg:p-20 border border-slate-700 shadow-2xl relative overflow-hidden">
                    {/* Background Grid */}
                    <div className="absolute inset-0 opacity-10"
                        style={{ backgroundImage: 'linear-gradient(#4f46e5 1px, transparent 1px), linear-gradient(90deg, #4f46e5 1px, transparent 1px)', backgroundSize: '40px 40px' }}
                    />

                    <div className="relative z-10 flex flex-col lg:flex-row items-center justify-between gap-12 lg:gap-0">

                        {/* 1. THE CLIENT */}
                        <div className="flex flex-col items-center gap-6 relative group">
                            <motion.div
                                animate={step === 0 || step === 4 ? { scale: [1, 1.02, 1], boxShadow: "0 0 30px rgba(59, 130, 246, 0.3)" } : { opacity: 0.5 }}
                                transition={{ repeat: Infinity, duration: 3 }}
                                className={`w-48 h-48 rounded-3xl flex flex-col items-center justify-center border-4 relative backdrop-blur-xl transition-all duration-500 ${step === 4 ? 'bg-green-500/10 border-green-500' : 'bg-blue-500/10 border-blue-500'
                                    }`}
                            >
                                <Globe size={64} className={step === 4 ? "text-green-400" : "text-blue-400"} />
                                <span className="font-bold text-2xl mt-4 text-white">Client</span>
                                <span className="text-sm font-mono text-slate-400">browser.exe</span>

                                {step === 4 && (
                                    <motion.div
                                        initial={{ scale: 0 }} animate={{ scale: 1 }}
                                        className="absolute -top-4 -right-4 bg-green-500 text-white p-2 rounded-full shadow-lg"
                                    >
                                        <CheckCircle size={24} />
                                    </motion.div>
                                )}
                            </motion.div>

                            {/* Action Button */}
                            {step === 0 && (
                                <motion.button
                                    whileHover={{ scale: 1.05 }}
                                    whileTap={{ scale: 0.95 }}
                                    initial={{ y: 20, opacity: 0 }}
                                    animate={{ y: 0, opacity: 1 }}
                                    onClick={handleSend}
                                    style={{ cursor: 'pointer' }}
                                    className="bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600 text-white font-bold px-8 py-3 rounded-full shadow-lg shadow-blue-500/30 flex items-center gap-3 text-lg"
                                >
                                    <Mail size={20} /> Send Request
                                </motion.button>
                            )}

                            {step === 4 && (
                                <motion.div
                                    initial={{ y: 20, opacity: 0 }}
                                    animate={{ y: 0, opacity: 1 }}
                                    className="bg-slate-700/50 text-green-300 font-mono text-sm px-6 py-2 rounded-full border border-green-500/30 flex items-center gap-2"
                                >
                                    <Package size={16} /> Data Received (200 OK)
                                </motion.div>
                            )}
                        </div>

                        {/* 2. THE NETWORK (CONNECTION) */}
                        <div className="flex-1 w-full lg:w-auto px-8 relative h-16 flex items-center">
                            {/* Cable */}
                            <div className="w-full h-2 bg-slate-700 rounded-full relative overflow-hidden">
                                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-slate-600 to-transparent animate-[shimmer_2s_infinite]" />
                            </div>

                            {/* Data Packets */}
                            <AnimatePresence mode="wait">
                                {step === 1 && (
                                    <motion.div
                                        initial={{ left: '0%', opacity: 0 }}
                                        animate={{ left: '90%', opacity: 1 }}
                                        exit={{ opacity: 0 }}
                                        transition={{ duration: 1.5, ease: "easeInOut" }}
                                        className="absolute top-1/2 -translate-y-1/2 flex flex-col items-center"
                                    >
                                        <div className="bg-orange-500 text-white p-3 rounded-xl shadow-lg shadow-orange-500/50 animate-pulse">
                                            <Mail size={24} />
                                        </div>
                                        <div className="text-xs font-mono font-bold text-orange-400 mt-2 bg-black/50 px-2 py-0.5 rounded">GET /pizza</div>
                                    </motion.div>
                                )}
                                {step === 3 && (
                                    <motion.div
                                        initial={{ left: '90%', opacity: 0 }}
                                        animate={{ left: '0%', opacity: 1 }}
                                        exit={{ opacity: 0 }}
                                        transition={{ duration: 1.5, ease: "easeInOut" }}
                                        className="absolute top-1/2 -translate-y-1/2 flex flex-col items-center"
                                    >
                                        <div className="bg-green-500 text-white p-3 rounded-xl shadow-lg shadow-green-500/50 animate-pulse">
                                            {/* GORILLA POSTMAN */}
                                            <img src="/assets/gorillas/gorilla_postman.png" className="w-12 h-12 object-contain" alt="Postman" />
                                        </div>
                                        <div className="text-xs font-mono font-bold text-green-400 mt-2 bg-black/50 px-2 py-0.5 rounded">JSON Data</div>
                                    </motion.div>
                                )}
                            </AnimatePresence>
                        </div>

                        {/* 3. THE SERVER */}
                        <div className="flex flex-col items-center gap-6 relative">
                            <motion.div
                                className={`w-48 h-48 rounded-3xl flex flex-col items-center justify-center border-4 relative backdrop-blur-xl ${serverProcessing ? 'bg-indigo-500/20 border-indigo-400 shadow-[0_0_50px_rgba(99,102,241,0.4)]' : 'bg-slate-800/50 border-slate-600'
                                    }`}
                            >
                                {serverProcessing ? (
                                    <div className="absolute inset-0 flex items-center justify-center">
                                        <div className="w-32 h-32 border-4 border-indigo-500 border-t-transparent rounded-full animate-spin" />
                                    </div>
                                ) : null}

                                <Server size={64} className={serverProcessing ? "text-indigo-300" : "text-slate-500"} />
                                <span className={`font-bold text-2xl mt-4 ${serverProcessing ? "text-indigo-200" : "text-slate-400"}`}>Server</span>
                                <span className="text-sm font-mono text-slate-600">api.cloud</span>

                                {serverProcessing && (
                                    <motion.div
                                        initial={{ opacity: 0, y: 10 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        className="absolute -bottom-16 bg-indigo-600 text-white px-4 py-2 rounded-lg text-sm font-bold shadow-lg"
                                    >
                                        Processing Request...
                                    </motion.div>
                                )}
                            </motion.div>
                        </div>

                    </div>
                </div>

                {/* Success Message / Footer */}
                <div className="h-24 mt-8 flex items-center justify-center">
                    <AnimatePresence>
                        {step === 4 && (
                            <motion.div
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                className="flex gap-4 items-center"
                            >
                                <img src="/assets/gorillas/gorilla_postman.png" className="w-16 h-16 object-contain drop-shadow-lg" alt="Postman" />
                                <div>
                                    <button
                                        onClick={reset}
                                        style={{ cursor: 'pointer' }}
                                        className="px-6 py-3 rounded-xl border border-slate-600 text-slate-400 font-bold hover:bg-slate-800 hover:text-white transition-colors flex items-center gap-2"
                                    >
                                        <Radio size={18} /> Replay
                                    </button>
                                </div>
                                <Link to="/" className="px-8 py-3 bg-green-600 hover:bg-green-500 text-white font-bold rounded-xl shadow-lg shadow-green-600/20 transition-all hover:scale-105 flex items-center gap-2">
                                    Next Module <ArrowRight size={20} />
                                </Link>
                            </motion.div>
                        )}
                        {step !== 4 && step !== 0 && (
                            <motion.p
                                initial={{ opacity: 0 }} animate={{ opacity: 1 }}
                                className="text-slate-500 font-mono animate-pulse"
                            >
                                Status: Establishing Connection...
                            </motion.p>
                        )}
                    </AnimatePresence>
                </div>

            </main>
        </div>
    );
};

export default ClientServerMap;
